import { useMemo } from "react";
import { motion } from "framer-motion";

// Short labels for the radial axis so 13 categories stay readable
const SHORT = {
  "Ball Mastery": "Ball Mastery",
  "Receiving & First Touch": "First Touch",
  "Turning": "Turning",
  "Dribbling & Ball Carrying": "Dribbling",
  "Feints & Skill Moves": "Skill Moves",
  "Passing": "Passing",
  "Crossing": "Crossing",
  "Finishing": "Finishing",
  "Aerial & Volleys": "Aerial",
  "Flair & Specialist": "Flair",
  "Set Pieces": "Set Pieces",
  "Defending": "Defending",
  "Game Combinations": "Combinations",
};

const ratingColor = (v, max) => {
  const p = v / max;
  if (v <= 0) return "#334155";
  if (p >= 0.85) return "#D4AF37";
  if (p >= 0.55) return "#2E6BE6";
  if (p >= 0.3) return "#38BDF8";
  return "#F59E0B";
};

export default function SkillWheel({ skills = [], categories = [], maxRating = 7, selected, onSelect }) {
  const stats = useMemo(() => {
    const map = {};
    categories.forEach((c) => (map[c] = { rated: [], total: 0 }));
    skills.forEach((s) => {
      if (!map[s.category]) map[s.category] = { rated: [], total: 0 };
      map[s.category].total += 1;
      if (s.rating > 0) map[s.category].rated.push(s.rating);
    });
    return categories.map((c) => {
      const m = map[c] || { rated: [], total: 0 };
      const avg = m.rated.length ? m.rated.reduce((a, b) => a + b, 0) / m.rated.length : 0;
      return { category: c, avg, ratedCount: m.rated.length, total: m.total };
    });
  }, [skills, categories]);

  const overall = useMemo(() => {
    const rated = skills.filter((s) => s.rating > 0);
    return rated.length ? (rated.reduce((a, s) => a + s.rating, 0) / rated.length) : 0;
  }, [skills]);

  const size = 420, c = size / 2, R = 145;
  const n = stats.length || 1;
  const angleFor = (i) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const point = (i, val) => {
    const r = R * (val / maxRating);
    const a = angleFor(i);
    return [c + r * Math.cos(a), c + r * Math.sin(a)];
  };
  const axisPoint = (i, mult = 1) => {
    const a = angleFor(i);
    return [c + R * mult * Math.cos(a), c + R * mult * Math.sin(a)];
  };

  const polygon = stats.map((s, i) => point(i, s.avg).join(",")).join(" ");
  const rings = [1, 2, 3, 4, 5, 6, 7];

  return (
    <div className="w-full flex flex-col items-center" data-testid="skill-wheel-container">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[440px]" style={{ overflow: "visible" }}>
        <defs>
          <radialGradient id="wheelFill" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#2E6BE6" stopOpacity="0.12" />
          </radialGradient>
        </defs>

        {/* concentric pitch rings */}
        {rings.map((lvl) => (
          <circle key={lvl} cx={c} cy={c} r={(R * lvl) / maxRating}
            fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        ))}

        {/* spokes */}
        {stats.map((s, i) => {
          const [x, y] = axisPoint(i);
          return <line key={i} x1={c} y1={c} x2={x} y2={y} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />;
        })}

        {/* data polygon */}
        {stats.length > 0 && (
          <motion.polygon
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            style={{ transformOrigin: `${c}px ${c}px` }}
            points={polygon}
            fill="url(#wheelFill)"
            stroke="#D4AF37"
            strokeWidth="2"
          />
        )}

        {/* category nodes + labels */}
        {stats.map((s, i) => {
          const [nx, ny] = point(i, s.avg);
          const [lx, ly] = axisPoint(i, 1.16);
          const isSel = selected === s.category;
          const anchor = Math.abs(lx - c) < 20 ? "middle" : lx > c ? "start" : "end";
          return (
            <g key={s.category} data-testid={`skill-category-spoke-${i}`}
              onClick={() => onSelect && onSelect(s.category)} style={{ cursor: "pointer" }}>
              {/* hit area */}
              <line x1={c} y1={c} {...{ x2: axisPoint(i)[0], y2: axisPoint(i)[1] }} stroke="transparent" strokeWidth="22" />
              <circle cx={nx} cy={ny} r={isSel ? 7 : 5} fill={ratingColor(s.avg, maxRating)}
                stroke={isSel ? "#fff" : "rgba(0,0,0,0.4)"} strokeWidth={isSel ? 2 : 1}
                style={isSel ? { filter: "drop-shadow(0 0 8px rgba(212,175,55,0.8))" } : {}} />
              <text x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle"
                fontSize="11" fontWeight={isSel ? 700 : 500}
                fill={isSel ? "#D4AF37" : "rgba(255,255,255,0.6)"}
                style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {SHORT[s.category] || s.category}
              </text>
            </g>
          );
        })}

        {/* center hub */}
        <circle cx={c} cy={c} r="34" fill="#0A0A0A" stroke="#D4AF37" strokeWidth="1.5" />
        <text x={c} y={c - 4} textAnchor="middle" fontSize="24" fontWeight="800" fill="#D4AF37" fontFamily="'Bebas Neue', sans-serif">
          {overall.toFixed(1)}
        </text>
        <text x={c} y={c + 12} textAnchor="middle" fontSize="8" fill="rgba(255,255,255,0.5)" style={{ letterSpacing: "1px" }}>
          OVR / {maxRating}
        </text>
      </svg>
      <p className="text-white/30 text-[11px] mt-2 text-center">Tap a category to see the drills inside it</p>
    </div>
  );
}
